import type { Plugin } from 'grapesjs';
import apis from '@api/api';
import { HOST } from '@api/constant';
import { store } from '@reduxLocal/persistState';
import parseByDelimiter from '@utils/parseByDelimiter';

export type ShareLinkCategory = 'dataset' | 'project' | 'calendar';

export interface ShareLinkOption {
	id: string;
	label: string;
	url: string;
	description?: string;
	meta?: Record<string, any>;
}

export type ShareLinkFetcher = () => Promise<ShareLinkOption[]>;

export interface ShareLinksPluginOptions {
	fetchers?: Partial<Record<ShareLinkCategory, ShareLinkFetcher>>;
	typeLabels?: Partial<Record<ShareLinkCategory, string>>;
	onError?: (error: unknown, category: ShareLinkCategory) => void;
}

type ShareLinkState = Record<ShareLinkCategory, ShareLinkOption[]>;

const DEFAULT_TYPE_LABELS: Record<ShareLinkCategory, string> = {
	dataset: 'Datasets',
	project: 'Projects',
	calendar: 'Calendars',
};

const COMPONENT_TYPE = 'share-link-button';
const COMPONENT_FLAG = 'data-share-link';
const REFRESH_EVENT = 'shareLinks:update';
const FALLBACK_HOST = 'https://api.smartsaas.pro';

type ThemePalette = {
	background: string;
	surface: string;
	text: string;
	subtleText: string;
	border: string;
	placeholder: string;
	primary: string;
	primaryText: string;
	danger: string;
};

const slugify = (value?: string) =>
	(value || '')
		.toString()
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');

const normalizeHost = (host?: string) => {
	if (!host) return FALLBACK_HOST;
	const trimmed = host.trim().replace(/\/+$/, '');
	if (/^https?:\/\//i.test(trimmed)) return trimmed;
	return `https://${trimmed}`;
};

const ensureTrailingSlash = (value: string) =>
	value.endsWith('/') ? value : `${value}/`;

const getThemePalette = (): ThemePalette => {
	try {
		const state = store.getState?.();
		const isLightTheme =
			state?.app?.theme?.lightTheme ?? state?.app?.theme?.light ?? true;

		if (isLightTheme) {
			return {
				background: '#ffffff',
				surface: '#f8fafc',
				text: '#1f2933',
				subtleText: '#4b5563',
				border: '#d1d5db',
				placeholder: '#9ca3af',
				primary: '#5ac0ea',
				primaryText: '#ffffff',
				danger: '#c0392b',
			};
		}

		return {
			background: '#1f2933',
			surface: '#111827',
			text: '#f1f5f9',
			subtleText: '#cbd5f5',
			border: '#374151',
			placeholder: '#6b7280',
			primary: '#5ac0ea',
			primaryText: '#0f172a',
			danger: '#ef4444',
		};
	} catch (error) {
		console.warn('[ShareLinksPlugin] Unable to determine theme', error);
		return {
			background: '#ffffff',
			surface: '#f8fafc',
			text: '#1f2933',
			subtleText: '#4b5563',
			border: '#d1d5db',
			placeholder: '#9ca3af',
			primary: '#5ac0ea',
			primaryText: '#ffffff',
			danger: '#c0392b',
		};
	}
};

const createDatasetLink = (item: any, host: string) => {
	const id = item?._id || item?.id;
	if (!id) return undefined;

	const existingUrl =
		item?.shareUrl ||
		item?.share_url ||
		item?.shareLink ||
		item?.share_link ||
		item?.url ||
		item?.href;
	if (existingUrl) return existingUrl;

	const title = item?.title || item?.dataTitle || item?.name || 'dataset';
	const slug = slugify(title);
	const base = ensureTrailingSlash(`${host}/forms/builder`);
	const resourceUrl = slug ? `${base}${slug}` : base.slice(0, -1);
	const joiner = resourceUrl.includes('?') ? '&' : '?';
	return `${resourceUrl}${joiner}id=${encodeURIComponent(id)}`;
};

const createProjectLink = (item: any, host: string) => {
	const id = item?._id || item?.id || item?.projectId;
	if (!id) return undefined;

	const existingUrl =
		item?.shareUrl ||
		item?.share_url ||
		item?.shareLink ||
		item?.share_link ||
		item?.url ||
		item?.href;
	if (existingUrl) return existingUrl;

	const title = item?.title || item?.name || item?.projectTitle || 'project';
	const slug = slugify(title);
	const base = ensureTrailingSlash(`${host}/projects`);
	const resourceUrl = slug ? `${base}${slug}` : base.slice(0, -1);
	const joiner = resourceUrl.includes('?') ? '&' : '?';
	return `${resourceUrl}${joiner}id=${encodeURIComponent(id)}`;
};

const createCalendarLink = (item: any, host: string) => {
	const id = item?._id || item?.id || item?.calendarId;
	if (!id) return undefined;

	const existingUrl =
		item?.shareUrl ||
		item?.share_url ||
		item?.shareLink ||
		item?.share_link ||
		item?.url ||
		item?.href;
	if (existingUrl) return existingUrl;

	const base = normalizeHost(host);
	const joiner = base.includes('?') ? '&' : '?';
	return `${base}/calendar/bookings${joiner}id=${encodeURIComponent(id)}`;
};

const toShareLinkOption = (
	item: any,
	fallbackLabel: string,
	urlBuilder: (value: any, host: string) => string | undefined,
	host: string
): ShareLinkOption | undefined => {
	const id = item?._id || item?.id || item?.calendarId || item?.projectId;
	if (!id) return undefined;

	const label = item?.title || item?.dataTitle || item?.name || fallbackLabel;
	const url = urlBuilder(item, host);
	if (!url) return undefined;

	return {
		id: id.toString(),
		label: label || fallbackLabel,
		url,
		meta: item,
	};
};

export const createShareLinkFetchers = (params: { userId?: string | null }) => {
	const host = normalizeHost(HOST);
	const resolveUserId = () => {
		if (params.userId) return params.userId;
		try {
			const state = store.getState();
			return (
				state?.app?.selectedProfile?.id ||
				state?.promiseData?.data?.userData?.id ||
				null
			);
		} catch (error) {
			console.warn(
				'[ShareLinksPlugin] Unable to resolve user ID from store',
				error
			);
			return null;
		}
	};

	const datasetFetcher: ShareLinkFetcher = async () => {
		const userId = resolveUserId();
		if (!userId) return [];
		try {
			const response = await apis.getData(userId);
			const raw = response as any;
			const entries = Array.isArray(raw)
				? raw
				: Array.isArray(raw?.data)
					? raw.data
					: Array.isArray(raw?.data?.data)
						? raw.data.data
						: Array.isArray(raw?.forms)
							? raw.forms
							: Array.isArray(raw?.records)
								? raw.records
								: [];

			if (!entries.length) {
				console.warn(
					'[ShareLinksPlugin] No datasets found for user',
					response
				);
			}

			return entries
				.map((item) =>
					toShareLinkOption(item, 'Dataset', createDatasetLink, host)
				)
				.filter((option): option is ShareLinkOption => Boolean(option));
		} catch (error) {
			console.error(
				'[ShareLinksPlugin] Failed to fetch dataset links',
				error
			);
			return [];
		}
	};

	const projectFetcher: ShareLinkFetcher = async () => {
		try {
			const response = await apis.getProjects();
			const raw = response as any;
			const projects = Array.isArray(raw)
				? raw
				: Array.isArray(raw?.data?.data)
					? raw.data.data
					: Array.isArray(raw?.data)
						? raw.data
						: Array.isArray(raw?.records)
							? raw.records
							: Array.isArray(raw?.projectList)
								? raw.projectList
								: Array.isArray(raw?.projects)
									? raw.projects
									: [];
			if (!Array.isArray(projects)) return [];
			return projects
				.map((item) =>
					toShareLinkOption(item, 'Project', createProjectLink, host)
				)
				.filter((option): option is ShareLinkOption => Boolean(option));
		} catch (error) {
			console.error(
				'[ShareLinksPlugin] Failed to fetch project links',
				error
			);
			return [];
		}
	};

	const calendarFetcher: ShareLinkFetcher = async () => {
		try {
			const calendars = await apis.getCalendars();
			const raw = calendars as any;
			const calendarEntries = Array.isArray(raw)
				? raw
				: Array.isArray(raw?.data)
					? raw.data
					: Array.isArray(raw?.records)
						? raw.records
						: Array.isArray(raw?.calendars)
							? raw.calendars
							: [];
			if (!Array.isArray(calendarEntries)) return [];
			return calendarEntries
				.map((item) =>
					toShareLinkOption(
						item,
						'Calendar',
						createCalendarLink,
						host
					)
				)
				.filter((option): option is ShareLinkOption => Boolean(option));
		} catch (error) {
			console.error(
				'[ShareLinksPlugin] Failed to fetch calendar links',
				error
			);
			return [];
		}
	};

	const fetchers: Partial<Record<ShareLinkCategory, ShareLinkFetcher>> = {
		dataset: datasetFetcher,
		project: projectFetcher,
		calendar: calendarFetcher,
	};

	return fetchers;
};

const ShareLinksPlugin: Plugin<ShareLinksPluginOptions> = (
	editor,
	opts = {}
) => {
	const typeLabels = { ...DEFAULT_TYPE_LABELS, ...(opts.typeLabels || {}) };

	const defaultFetchers = createShareLinkFetchers({});
	const fetchers: Partial<Record<ShareLinkCategory, ShareLinkFetcher>> = {
		...defaultFetchers,
		...(opts.fetchers || {}),
	};

	const state: ShareLinkState = {
		dataset: [],
		project: [],
		calendar: [],
	};

	const loading: Partial<
		Record<ShareLinkCategory, Promise<ShareLinkOption[]>>
	> = {};

	const ensureLoaded = async (category: ShareLinkCategory) => {
		if (!fetchers[category]) {
			state[category] = [];
			return state[category];
		}

		if (!loading[category]) {
			loading[category] = fetchers[category]!()
				.then((items) => {
					state[category] = Array.isArray(items) ? items : [];
					return state[category];
				})
				.catch((error) => {
					opts.onError?.(error, category);
					console.error(
						`[ShareLinksPlugin] Failed to load ${category} links`,
						error
					);
					state[category] = [];
					return state[category];
				})
				.finally(() => {
					delete loading[category];
				});
		}

		return loading[category]!;
	};

	const getOptionsForCategory = (category: ShareLinkCategory) =>
		state[category]?.map(({ id, label }) => ({ id, label })) || [];

	const refreshComponents = (category?: ShareLinkCategory) => {
		const wrapper = editor.getWrapper();
		if (!wrapper) return;
		const components = wrapper.find(`[${COMPONENT_FLAG}="true"]`);
		components.forEach((component) => {
			component.trigger(REFRESH_EVENT, category);
		});
	};

	const categoryOptions = (
		Object.keys(typeLabels) as ShareLinkCategory[]
	).map((category) => ({
		id: category,
		label: typeLabels[category],
	}));
	const fallbackCategory: ShareLinkCategory =
		categoryOptions[0]?.id || 'dataset';

	const blockDefinitions: Array<{
		id: string;
		label: string;
		defaultCategory: ShareLinkCategory;
		description: string;
		icon: string;
	}> = [
		{
			id: 'share-link-dataset',
			label: 'Dataset Link',
			defaultCategory: 'dataset',
			description: 'Insert a button linked to a dataset or form',
			icon: 'fa fa-database',
		},
		{
			id: 'share-link-project',
			label: 'Project Link',
			defaultCategory: 'project',
			description: 'Insert a button linked to a project',
			icon: 'fa fa-briefcase',
		},
		{
			id: 'share-link-calendar',
			label: 'Calendar Link',
			defaultCategory: 'calendar',
			description: 'Insert a button linked to a booking page',
			icon: 'fa fa-calendar',
		},
	];

	blockDefinitions.forEach(
		({ id, label, defaultCategory, description, icon }) => {
			editor.BlockManager.add(id, {
				label,
				category: 'Smart Links',
				media: `<div style="display:flex;flex-direction:column;align-items:center;gap:6px;font-size:12px;">
          <i class="${icon}" style="font-size:24px;"></i>
          <span>${label}</span>
        </div>`,
				content: {
					type: COMPONENT_TYPE,
					components: label,
					shareLinkCategory: defaultCategory,
				},
				attributes: { class: icon },
			});
		}
	);

	editor.DomComponents.addType(COMPONENT_TYPE, {
		isComponent: (el) => el?.getAttribute?.(COMPONENT_FLAG) === 'true',
		model: {
			defaults: {
				tagName: 'a',
				[COMPONENT_FLAG]: 'true',
				attributes: {
					[COMPONENT_FLAG]: 'true',
					class: 'share-link-button',
					href: '#',
					target: '_blank',
					rel: 'noopener noreferrer',
				},
				shareLinkCategory: fallbackCategory,
				shareLinkItem: '',
				stylable: true,
				draggable: true,
				droppable: false,
				selectable: true,
				traits: [
					{
						type: 'button',
						name: 'openResourceSelector',
						label: 'Select Resource',
						changeProp: false,
						command: (cmdEditor, trait) => {
							const target =
								(trait as any)?.target ||
								cmdEditor?.getSelected?.();
							const component = Array.isArray(target)
								? target[0]
								: target;
							component?.openSelectionModal?.();
						},
					},
					{
						type: 'select',
						name: 'shareLinkItem',
						label: 'Resource',
						changeProp: true,
						options: [],
					},
					{
						type: 'text',
						name: 'href',
						label: 'URL',
						placeholder: 'https://...',
						changeProp: true,
					},
					{
						type: 'checkbox',
						name: 'openInNewTab',
						label: 'Open in new tab',
						valueTrue: '_blank',
						valueFalse: '_self',
						changeProp: true,
					},
				],
				styles: `
          display: inline-block;
          padding: 12px 24px;
          background: #5ac0ea;
          color: #ffffff;
          border-radius: 6px;
          text-decoration: none;
          font-weight: 600;
          text-align: center;
        `,
			},
			init() {
				this.listenTo(
					this,
					`change:shareLinkCategory`,
					this.handleCategoryChange
				);
				this.listenTo(
					this,
					`change:shareLinkItem`,
					this.handleItemChange
				);
				this.listenTo(
					this,
					`change:openInNewTab`,
					this.handleTargetChange
				);
				this.listenTo(this, REFRESH_EVENT, this.handleExternalRefresh);

				const initialCategory =
					(this.get('shareLinkCategory') as ShareLinkCategory) ||
					fallbackCategory;

				ensureLoaded(initialCategory).then(() => {
					this.updateTraitOptions(initialCategory);
					if (!this.get('shareLinkItem')) {
						setTimeout(() => this.openSelectionModal(), 50);
					}
				});
			},
			openSelectionModal() {
				const modal = editor.Modal;
				const palette = getThemePalette();
				const container = document.createElement('div');
				container.className = 'share-links-modal';
				container.style.cssText =
					`display:flex;flex-direction:column;gap:12px;padding:18px;min-width:340px;` +
					`background:${palette.surface};color:${palette.text};` +
					`border:1px solid ${palette.border};border-radius:12px;` +
					`box-shadow:0 18px 48px rgba(15,23,42,0.35);`;

				const description = document.createElement('p');
				description.textContent =
					'Choose the resource you want this button to link to.';
				description.style.margin = '0';
				description.style.fontSize = '13px';
				description.style.color = palette.subtleText;

				const categoryWrapper = document.createElement('div');
				categoryWrapper.style.cssText =
					'display:flex;flex-direction:column;gap:6px;';
				const categoryLabel = document.createElement('label');
				categoryLabel.textContent = 'Source';
				categoryLabel.style.fontSize = '12px';
				categoryLabel.style.fontWeight = '600';
				categoryLabel.style.color = palette.subtleText;
				const categorySelect = document.createElement('select');
				categorySelect.style.cssText =
					`padding:8px;border-radius:6px;border:1px solid ${palette.border};font-size:13px;` +
					`background:${palette.background};color:${palette.text};`;
				categoryOptions.forEach(({ id, label }) => {
					const option = document.createElement('option');
					option.value = id;
					option.textContent = label;
					categorySelect.appendChild(option);
				});
				const currentCategory =
					(this.get('shareLinkCategory') as ShareLinkCategory) ||
					fallbackCategory;
				if (categoryOptions.some(({ id }) => id === currentCategory)) {
					categorySelect.value = currentCategory;
				}
				categoryWrapper.appendChild(categoryLabel);
				categoryWrapper.appendChild(categorySelect);

				const resourceWrapper = document.createElement('div');
				resourceWrapper.style.cssText =
					'display:flex;flex-direction:column;gap:6px;';
				const resourceLabel = document.createElement('label');
				resourceLabel.textContent = 'Resource';
				resourceLabel.style.fontSize = '12px';
				resourceLabel.style.fontWeight = '600';
				resourceLabel.style.color = palette.subtleText;
				const resourceSelect = document.createElement('select');
				resourceSelect.style.cssText =
					`padding:8px;border-radius:6px;border:1px solid ${palette.border};font-size:13px;` +
					`background:${palette.background};color:${palette.text};`;
				resourceWrapper.appendChild(resourceLabel);
				resourceWrapper.appendChild(resourceSelect);

				const status = document.createElement('div');
				status.style.cssText = `font-size:12px;color:${palette.subtleText};min-height:18px;margin-top:4px;`;

				const actions = document.createElement('div');
				actions.style.cssText =
					'display:flex;justify-content:flex-end;gap:8px;margin-top:12px;';
				const cancelBtn = document.createElement('button');
				cancelBtn.type = 'button';
				cancelBtn.textContent = 'Cancel';
				cancelBtn.style.cssText =
					`padding:8px 14px;border-radius:6px;border:1px solid ${palette.border};` +
					`background:${palette.background};color:${palette.text};cursor:pointer;`;
				const confirmBtn = document.createElement('button');
				confirmBtn.type = 'button';
				confirmBtn.textContent = 'Apply Link';
				confirmBtn.style.cssText =
					`padding:8px 14px;border-radius:6px;border:none;` +
					`background:${palette.primary};color:${palette.primaryText};cursor:pointer;font-weight:600;`;

				actions.appendChild(cancelBtn);
				actions.appendChild(confirmBtn);

				container.appendChild(description);
				container.appendChild(categoryWrapper);
				container.appendChild(resourceWrapper);
				container.appendChild(status);
				container.appendChild(actions);

				modal.setTitle('Select Smart Link');
				modal.setContent(container);
				modal.open();

				const setStatus = (
					message: string,
					tone: 'default' | 'error' = 'default'
				) => {
					status.textContent = message;
					status.style.color =
						tone === 'error' ? palette.danger : palette.subtleText;
				};

				const populateResources = async (
					categoryValue: ShareLinkCategory
				) => {
					this.set('shareLinkCategory', categoryValue);
					this.components(
						`Select a ${typeLabels[categoryValue] || 'resource'}`
					);
					setStatus('Loading resources…');
					resourceSelect.disabled = true;
					resourceSelect.innerHTML = '';
					const placeholder = document.createElement('option');
					placeholder.value = '';
					placeholder.textContent = 'Select resource';
					placeholder.disabled = true;
					placeholder.selected = true;
					placeholder.style.color = palette.placeholder;
					resourceSelect.appendChild(placeholder);

					try {
						const items = await ensureLoaded(categoryValue);
						if (!items.length) {
							setStatus(
								'No resources available for this category.'
							);
							resourceSelect.disabled = true;
							return;
						}

						items.forEach(({ id, label }) => {
							const option = document.createElement('option');
							option.value = id;
							option.textContent = label;
							resourceSelect.appendChild(option);
						});

						const currentItem = this.get('shareLinkItem');
						if (
							currentItem &&
							items.some(({ id }) => id === currentItem)
						) {
							resourceSelect.value = currentItem;
						} else {
							resourceSelect.value = '';
						}

						resourceSelect.disabled = false;
						resourceSelect.removeAttribute('disabled');
						this.updateTraitOptions(categoryValue);
						setStatus('');
					} catch (error) {
						console.error(
							'[ShareLinksPlugin] Failed to load resources',
							error
						);
						setStatus(
							'Unable to load resources. Please try again.',
							'error'
						);
					}
				};

				categorySelect.addEventListener('change', () => {
					const selectedCategory =
						categorySelect.value as ShareLinkCategory;
					this.set('shareLinkCategory', selectedCategory);
					this.set('shareLinkItem', '');
					populateResources(selectedCategory);
				});

				cancelBtn.addEventListener('click', () => {
					modal.close();
					this.remove();
				});

				confirmBtn.addEventListener('click', () => {
					const selectedCategory =
						categorySelect.value as ShareLinkCategory;
					const resourceId = resourceSelect.value;
					if (!resourceId) {
						setStatus(
							'Please select a resource before applying.',
							'error'
						);
						return;
					}

					const selectedOption =
						resourceSelect.options[resourceSelect.selectedIndex];
					const selectedLabel =
						selectedOption?.textContent || 'Open Link';

					if (this.get('shareLinkCategory') !== selectedCategory) {
						this.set('shareLinkCategory', selectedCategory);
					}
					this.set('shareLinkItem', resourceId);
					this.components(selectedLabel);
					this.handleItemChange();

					modal.close();
				});

				populateResources(categorySelect.value as ShareLinkCategory);
			},
			handleExternalRefresh(category?: ShareLinkCategory) {
				const activeCategory =
					category ||
					(this.get('shareLinkCategory') as ShareLinkCategory);
				if (!activeCategory) return;
				this.updateTraitOptions(activeCategory);
			},
			handleCategoryChange() {
				const category = this.get(
					'shareLinkCategory'
				) as ShareLinkCategory;
				if (!category) return;
				ensureLoaded(category).then(() => {
					this.updateTraitOptions(category);
				});
			},
			handleItemChange() {
				const category = this.get(
					'shareLinkCategory'
				) as ShareLinkCategory;
				const selected = this.get('shareLinkItem');
				if (!category || !selected) return;

				const item = state[category]?.find(({ id }) => id === selected);
				if (!item) return;

				this.addAttributes({ href: item.url });
				this.components([]);
				this.append({
					type: 'textnode',
					content: parseByDelimiter(item.label || 'Open Link'),
				});
				this.addStyle({
					'display': 'inline-block',
					'padding': '12px 24px',
					'background': '#5ac0ea',
					'color': '#ffffff',
					'border-radius': '6px',
					'text-decoration': 'none',
					'font-weight': '600',
					'text-align': 'center',
					'box-shadow': '0 6px 16px rgba(90, 192, 234, 0.35)',
					'transition': 'transform 0.2s ease, box-shadow 0.2s ease',
				});
				this.addAttributes({
					onmouseover:
						"this.style.transform='translateY(-1px)';this.style.boxShadow='0 8px 20px rgba(90,192,234,0.45)';",
				});
				this.addAttributes({
					onmouseout:
						"this.style.transform='';this.style.boxShadow='0 6px 16px rgba(90,192,234,0.35)';",
				});
				const hrefTrait = this.getTrait('href');
				hrefTrait?.setValue(item.url);
			},
			handleTargetChange() {
				const target = this.get('openInNewTab') || '_self';
				this.addAttributes({ target });
			},
			updateTraitOptions(category: ShareLinkCategory) {
				const trait = this.getTrait('shareLinkItem');
				if (!trait) return;

				const options = getOptionsForCategory(category);
				trait.set('options', options);
				trait.trigger('change:options', trait, options);
				if (trait.view) {
					trait.view.render();
				}

				if (options.length === 0) {
					this.set('shareLinkItem', '');
					this.components(
						`Select a ${typeLabels[category] || 'resource'}`
					);
					const hrefTrait = this.getTrait('href');
					hrefTrait?.setValue('');
					this.addAttributes({ href: '#' });
					return;
				}

				const current = this.get('shareLinkItem');
				const isValid = options.some(({ id }) => id === current);
				if (!isValid) {
					this.set('shareLinkItem', '');
					this.components(
						`Select a ${typeLabels[category] || 'resource'}`
					);
					const hrefTrait = this.getTrait('href');
					hrefTrait?.setValue('');
					this.addAttributes({ href: '#' });
					return;
				}
				this.handleItemChange();
			},
		},
	});

	(Object.keys(fetchers) as ShareLinkCategory[])
		.filter((category) => Boolean(fetchers[category]))
		.forEach((category) => {
			ensureLoaded(category).then(() => {
				refreshComponents(category);
			});
		});
};

export default ShareLinksPlugin;
